import { DownloadIcon, Loader2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useIntranetAcknowledgementReport } from '@/products/intranet/features/intranet/use-intranet-ack';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import { INTRANET_EDITORIAL_PATH, intranetEditPath } from '@/products/intranet/intranet-paths';

export function AcknowledgementsReportPage() {
  const { slug } = useParams();
  const report = useIntranetAcknowledgementReport(slug);
  usePageTitle(report.data ? `Bestätigungen: ${report.data.title}` : 'Lesebestätigungen');

  if (report.isPending) {
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Auswertung wird geladen …
      </p>
    );
  }

  if (report.isError || !report.data) {
    const missing =
      report.error instanceof ApiRequestError && report.error.error.statusCode === 404;
    return (
      <section className="space-y-4">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={INTRANET_EDITORIAL_PATH}>Zurück zur Redaktion</Link>
        </Button>
        <p role="alert" className="text-sm text-destructive">
          {missing
            ? 'Diesen Beitrag gibt es nicht.'
            : 'Die Auswertung konnte nicht geladen werden.'}
        </p>
      </section>
    );
  }

  const data = report.data;
  const rows = [
    ...data.items.map((item) => ({
      userId: item.userId,
      displayName: item.displayName,
      acknowledgedAt: item.acknowledgedAt,
    })),
    ...data.pending.map((item) => ({
      userId: item.userId,
      displayName: item.displayName,
      acknowledgedAt: null as string | null,
    })),
  ];

  function downloadCsv() {
    const header = 'Name;Bestätigt am';
    const lines = rows.map((row) =>
      [
        csvCell(row.displayName),
        csvCell(row.acknowledgedAt ? formatDateTime(row.acknowledgedAt) : ''),
      ].join(';'),
    );
    const blob = new Blob([`\uFEFF${[header, ...lines].join('\r\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lesebestaetigung-${data.slug}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Lesebestätigungen"
        description={data.title}
        action={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={intranetEditPath(data.slug)}>Beitrag bearbeiten</Link>
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={downloadCsv}>
              <DownloadIcon aria-hidden />
              CSV exportieren
            </Button>
          </div>
        }
      />

      <p className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <Badge variant="secondary">{data.completionRate} %</Badge>
        <span>
          {data.acknowledgedCount} von {data.audienceSize} bestätigt
        </span>
        {data.ackDeadline ? <span>Frist {formatDateTime(data.ackDeadline)}</span> : null}
      </p>

      {rows.length === 0 ? (
        <p
          className={`${intranetPanelClassName} border-dashed px-4 py-10 text-center text-sm text-muted-foreground`}
        >
          Niemand in der Zielgruppe.
        </p>
      ) : (
        <div className={intranetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bestätigt am</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell className="font-medium whitespace-normal">{row.displayName}</TableCell>
                  <TableCell>
                    {row.acknowledgedAt ? (
                      <Badge>Bestätigt</Badge>
                    ) : (
                      <Badge variant="outline">Offen</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.acknowledgedAt ? formatDateTime(row.acknowledgedAt) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data.leavers.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium">Nicht mehr in der Zielgruppe</h2>
          <p className="text-sm text-muted-foreground">
            Diese Bestätigungen zählen nicht in die Quote.
          </p>
          <ul className={`${intranetPanelClassName} divide-y px-4 text-sm`}>
            {data.leavers.map((leaver) => (
              <li key={leaver.userId} className="flex justify-between gap-3 py-2">
                <span>{leaver.displayName}</span>
                <span className="text-muted-foreground">
                  {formatDateTime(leaver.acknowledgedAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

function csvCell(value: string): string {
  if (/[;"\r\n]/.test(value)) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}
